import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch, getAdminAuthToken } from '../jscontrol/apiClient';
import { getApiBase, getImageSrc, getAddBannerAuditUser, parseUploadImageUrl } from './addBannerUtils';
import '../css/Category.css';

const AddBannerEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    Title: '',
    RedirectUrl: '',
    DisplayOrder: 0,
    IsActive: true,
  });
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    const fetchBanner = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data } = await apiFetch(`/AddBanner/GetById/${id}`);
        const b = data?.data ?? data;
        setForm({
          Title: b.Title || '',
          RedirectUrl: b.RedirectUrl || '',
          DisplayOrder: b.DisplayOrder ?? 0,
          IsActive: b.IsActive !== undefined ? b.IsActive : true,
        });
        setOriginalImageUrl(b.ImageUrl || '');
        setPreviewUrl(getImageSrc(b.ImageUrl));
      } catch (e) {
        setLoadError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchBanner();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const uploadFile = async (file) => {
    const baseUrl = getApiBase();
    const token = getAdminAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${baseUrl}/AddBanner/Upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || json?.success === false) {
      throw new Error(json?.message || json?.Message || `Upload failed (${res.status})`);
    }
    return parseUploadImageUrl(json);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    if (!form.Title.trim()) { setFormError('Title is required.'); return; }

    setSaving(true);
    try {
      let imageUrl = originalImageUrl;

      if (selectedFile) {
        setUploading(true);
        imageUrl = await uploadFile(selectedFile);
        setUploading(false);
        if (!imageUrl) throw new Error('Upload succeeded but no image URL returned.');
      }

      await apiFetch('/AddBanner/Update', {
        method: 'PUT',
        data: {
          AddBannerId: Number(id),
          Title: form.Title,
          ImageUrl: imageUrl,
          RedirectUrl: form.RedirectUrl,
          DisplayOrder: Number(form.DisplayOrder) || 0,
          IsActive: form.IsActive,
          UpdatedBy: getAddBannerAuditUser(),
        },
      });
      showToast('Add banner updated successfully.');
    } catch (e) {
      setUploading(false);
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <AdminLink to="/admin/addbanner">Add Banners</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Edit</span>
      </p>

      <div className="row">
        <div className="col-lg-12 col-md-10 col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-1">Edit Add Banner</h4>
              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Update add banner details</p>

              {toast && (
                <div className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`} role="alert" style={{ fontSize: '0.875rem' }}>
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} />
                </div>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : loadError ? (
                <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
                  <i className="mdi mdi-alert-circle me-1"></i>{loadError}
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {formError && (
                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      <i className="mdi mdi-alert-circle me-1"></i>{formError}
                    </div>
                  )}

                  <div className="form-group mb-4">
                    <label className="form-label">
                      Banner Image
                      <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>(click to change)</span>
                    </label>
                    <div
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      style={{
                        border: '2px dashed #6f42c1',
                        borderRadius: 8,
                        padding: '32px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: '#faf8ff',
                        transition: 'background 0.15s',
                        maxWidth: 500,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0eaff'}
                      onMouseLeave={e => e.currentTarget.style.background = '#faf8ff'}
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="preview"
                          style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                        />
                      ) : (
                        <>
                          <i className="mdi mdi-cloud-upload" style={{ fontSize: '2.5rem', color: '#6f42c1' }}></i>
                          <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#6c757d' }}>Click to select a banner image</div>
                          <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>JPG, PNG, WEBP supported</div>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    {selectedFile && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0 mt-1"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl(getImageSrc(originalImageUrl));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        <i className="mdi mdi-close me-1"></i>Revert to original
                      </button>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Title <span className="text-danger">*</span></label>
                        <input className="form-control" name="Title" value={form.Title} onChange={handleChange} placeholder="Banner title" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Redirect URL</label>
                        <input className="form-control" name="RedirectUrl" value={form.RedirectUrl} onChange={handleChange} placeholder="https://..." />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Display Order</label>
                        <input className="form-control" type="number" name="DisplayOrder" value={form.DisplayOrder} onChange={handleChange} min={0} />
                      </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-end pb-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="IsActive" id="addbanner_edit_IsActive" checked={form.IsActive} onChange={handleChange} />
                        <label className="form-check-label" htmlFor="addbanner_edit_IsActive">Active</label>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-2 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                    <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
                      {uploading
                        ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Uploading...</>
                        : saving
                        ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving...</>
                        : 'Update'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/addbanner')}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddBannerEdit;
